// src/pages/services/HerbalRemedies.jsx
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import heroImg from '../../assets/hero-bg4.png';
import doctorImg from '../../assets/doctors4.png';
import Navbar from '../../common/Navbar';
import Footer from '../../common/Footer';
import image1 from '../../assets/hero1-HR.webp';
import image2 from '../../assets/hero-2-HR.avif';
import image4 from '../../assets/hero4-HR.webp';
import image5 from '../../assets/hero5-HR.webp';
import image6 from '../../assets/hero6-HR.webp';
import image7 from '../../assets/hero8-HR.png';
import quotebg from '../../assets/banner-bg.png';
import leaf from '../../assets/leaf1.png';

const HerbalRemedies = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen overflow-hidden bg-gradient-to-b from-stone-50 to-amber-50 text-sm md:text-base leading-relaxed">
      <Navbar />

      {/* Hero Section - Modern Parallax Style */}
      <section className="relative h-screen flex flex-col items-center justify-center overflow-hidden px-4 sm:px-6">
        {/* Main Hero Background */}
        <div className="absolute inset-0">
          <img
            src={heroImg}
            alt="Herbal Remedies"
            className="w-full h-full object-cover brightness-75 scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20" />
        </div>

        {/* Floating Herbal Elements */}
        <motion.img
          src={image1}
          alt="Ancient Mortar"
          className="absolute bottom-10 left-4 sm:left-8 w-40 sm:w-64 z-10"
          initial={{ opacity: 0, x: -200, y: 100, rotate: -30 }}
          whileInView={{ opacity: 1, x: -40, y: 50, rotate: 0 }}
          transition={{ duration: 2, ease: "easeOut" }}
          viewport={{ once: false, amount: 0.2 }}
        />

        <motion.div
          className="absolute top-10 left-24 sm:left-32 w-24 sm:w-40 rotate-12"
          initial={{ opacity: 0, scale: 0, rotate: -30 }}
          whileInView={{ opacity: 1, scale: 1, rotate: -12 }}
          transition={{ duration: 1.8, delay: 0.6 }}
          viewport={{ once: false }}
        >
          <img src={image2} alt="Nadi Pariksha" className="rotate-12" />
        </motion.div>

        <motion.img
          src={image4}
          alt="2500 Years of Wisdom"
          className="absolute top-8 right-4 sm:right-10 w-40 sm:w-64 -rotate-12"
          initial={{ opacity: 0, x: 300, rotate: 30 }}
          whileInView={{ opacity: 1, x: 0, rotate: -12 }}
          transition={{ duration: 2.2, delay: 0.4 }}
          viewport={{ once: false }}
        />

        <motion.div
          className="absolute bottom-20 left-1/4 w-24 sm:w-36"
          initial={{ opacity: 0, scale: 0, rotate: 20 }}
          whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1.8, delay: 1.2 }}
          viewport={{ once: false }}
        >
          <img src={image5} alt="Abhyanga Therapy" />
        </motion.div>

        <motion.div
          className="absolute bottom-40 right-20 sm:right-40 w-28 sm:w-44 -translate-y-20"
          initial={{ opacity: 0, x: 200, scale: 0 }}
          whileInView={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ duration: 2, delay: 1 }}
          viewport={{ once: false }}
        >
          <img src={image6} alt="Custom Herbal Blend" />
        </motion.div>

        <motion.img
          src={image7}
          alt="Healing Flower"
          className="absolute bottom-0 right-2 sm:right-5 w-28 sm:w-44"
          initial={{ opacity: 0, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 2.5, delay: 1.4 }}
        />

        <motion.div
          className="absolute top-1/2 right-24 sm:right-32 w-64 h-64 opacity-0"
          initial={{ opacity: 0, scale: 0 }}
          whileInView={{ opacity: 0.15, scale: 1 }}
          transition={{ duration: 3, delay: 1.2 }}
        >
          <div className="w-full h-full bg-gradient-to-br from-emerald-400/20 to-amber-300/20 rounded-full blur-3xl animate-pulse" />
        </motion.div>

        {/* Main Text – Slightly Reduced for Elegance */}
        <motion.div
          className="relative z-10 text-center px-4 sm:px-8 max-w-6xl mx-auto flex flex-col items-center"
          initial={{ opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.6, ease: "easeOut" }}
        >
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-playfair text-white tracking-tight leading-tight">
            Herbal
            <span className="block text-5xl md:text-7xl lg:text-8xl text-amber-100">
              Remedies
            </span>
          </h1>
          <p className="mt-6 text-lg md:text-2xl lg:text-3xl font-light text-amber-50/95 max-w-xs sm:max-w-4xl text-center leading-relaxed">
            Ancient wisdom meets modern healing.<br className="hidden md:block" />
            Pure, potent herbs tailored to your unique constitution.
          </p>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/contact")}
            className="mt-8 sm:mt-12 bg-amber-600/20 backdrop-blur-md border-2 border-amber-300/40 text-white px-8 sm:px-12 py-3 sm:py-5 rounded-full text-base sm:text-lg md:text-xl font-medium shadow-2xl hover:bg-amber-600/30 transition-all duration-300"
          >
            Begin Your Healing Journey
          </motion.button>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2 }}
            className="mt-6 sm:mt-10"
          >
            {/* <button
              onClick={() => {
                if (document.referrer.includes(window.location.origin)) {
                  window.history.back();
                } else {
                  window.location.href = "/";
                }
              }}
              className="group flex items-center gap-3 mx-auto text-amber-200 hover:text-white transition-all duration-300 text-sm md:text-base font-medium backdrop-blur-sm bg-white/5 px-6 sm:px-8 py-3 sm:py-4 rounded-full border border-amber-300/30 hover:border-amber-300/60 hover:bg-white/10"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to All Services
            </button> */}
          </motion.div>
        </motion.div>

        <motion.div
          animate={{ y: [0, 15, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 text-amber-200"
        >
          <svg className="w-8 h-8 sm:w-10 sm:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </motion.div>
      </section>

      {/* BENEFITS SECTION */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 relative overflow-hidden bg-gradient-to-b from-amber-50/30 to-white">
        <div className="max-w-7xl mx-auto flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12 sm:mb-20"
          >
            <h2 className="text-3xl text-center sm:text-4xl md:text-5xl font-playfair text-[#0B2A2C] mb-10 sm:mb-16">
              Healing Benefits
            </h2>
            <p className="mt-4 sm:mt-6 text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Experience the transformative power of authentic Ayurvedic herbs
            </p>
          </motion.div>

          <div className="flex flex-col sm:flex-row sm:flex-wrap justify-center items-stretch gap-6 sm:gap-10 w-full">
            {[
              { icon: "", title: "100% Organic", desc: "Sourced from pristine Himalayan & Kerala farms" },
              { icon: "", title: "Dosha-Specific", desc: "Custom blends for Vata, Pitta & Kapha balance" },
              { icon: "", title: "Deep Detox", desc: "Removes accumulated toxins gently & safely" },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 80 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group relative flex-1 min-w-[220px] bg-white/90 backdrop-blur-xl border border-amber-100/50 rounded-3xl p-6 sm:p-10 shadow-2xl hover:shadow-3xl transition-all duration-500 flex flex-col items-center"
              >
                <div className="text-5xl sm:text-6xl mb-4 text-amber-600">{item.icon}</div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#0B2A2C] mb-2 text-center">{item.title}</h3>
                <p className="text-gray-700 text-sm sm:text-base text-center">{item.desc}</p>
                <img src={leaf} alt="" className="absolute -top-6 -right-6 w-24 opacity-10 group-hover:opacity-20 transition-opacity" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS SECTION */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-amber-50 to-stone-100">
  <div className="max-w-5xl mx-auto">
    <motion.h2
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      className="text-3xl sm:text-4xl md:text-4xl font-playfair text-[#0B2A2C] mb-10 sm:mb-16 text-center"
    >
      Your Healing Journey
    </motion.h2>

    <div className="relative">
      {/* Vertical timeline line - only on desktop */}
      <div className="hidden lg:block absolute left-1/2 -translate-x-1/2 w-1 h-full bg-gradient-to-b from-amber-400 via-emerald-500 to-teal-600 opacity-30" />

      <div className="space-y-12 sm:space-y-16 lg:space-y-20">
        {[
          { title: "Dosha Assessment", desc: "In-depth pulse diagnosis and prakriti analysis" },
          { title: "Herb Selection & Blending", desc: "Handpicked premium herbs blended exclusively for you" },
          { title: "Daily Protocol Guidance", desc: "Personalized daily routine with diet & lifestyle tips" },
          { title: "Progress Review & Refinement", desc: "Monthly follow-ups to optimize your progress" },
        ].map((step, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: i % 2 === 0 ? -60 : 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: i * 0.15 }}
            className={`flex flex-col lg:flex-row items-center lg:items-start gap-5 lg:gap-8
              ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
          >
            {/* Smaller Number Circle */}
            <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 lg:w-20 lg:h-20 
              bg-gradient-to-br from-[#0B2A2C] to-[#1A4D4F] 
              rounded-full flex items-center justify-center 
              text-white text-xl sm:text-2xl lg:text-2xl font-bold 
              shadow-xl border-4 relative z-10">
              {i + 1}
              <div className="absolute inset-0 bg-amber-300/30 rounded-full blur-2xl animate-pulse -z-10" />
            </div>

            {/* Smaller Card */}
            <div className="w-full max-w-md lg:max-w-lg">
              <div className="bg-white/95 backdrop-blur-md border border-amber-100 
                rounded-2xl p-5 sm:p-6 lg:p-7 
                shadow-lg hover:shadow-xl hover:-translate-y-1 
                transition-all duration-400 min-h-[110px] sm:min-h-[120px] lg:min-h-[130px]
                flex flex-col justify-center">
                <h3 className="text-lg sm:text-xl lg:text-xl font-bold text-[#0B2A2C] mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                  {step.desc}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </div>
</section>

      {/* QUOTE SECTION */}
      <section className="py-32 sm:py-40 px-4 sm:px-6 relative overflow-hidden">
        <img
          src={quotebg || heroImg}
          alt="Background"
          className="absolute inset-0 w-full h-full object-cover brightness-50"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-[#0B2A2C]/80 to-black/70" />

        <div className="relative z-10 max-w-6xl mx-auto text-center flex flex-col items-center text-white">
          <motion.img
            initial={{ scale: 0.8, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2 }}
            src={doctorImg}
            alt="Dr. Rao"
            className="w-36 h-36 sm:w-64 sm:h-64 rounded-full mx-auto mb-6 sm:mb-12 border-4 sm:border-8 border-amber-300/30 shadow-2xl"
          />

          <motion.blockquote
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4 }}
className="text-2xl md:text-4xl font-joan italic max-w-4xl mx-auto leading-relaxed"
          >
            “Every herb has a purpose. When used with knowledge,<br />
            it becomes medicine for body, mind and soul.”
          </motion.blockquote>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 0.9 }}
            transition={{ delay: 0.8 }}
            className="mt-4 sm:mt-12 text-base sm:text-xl text-amber-200"
          >
            — Dr. V. K. Subrahmanyeswara Rao
          </motion.p>

          <img src={leaf} className="absolute top-20 left-10 w-40 opacity-10 animate-float" />
          <img src={leaf} className="absolute bottom-32 right-16 w-48 opacity-10 animate-float" style={{ animationDelay: '2s' }} />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 sm:py-40 px-6 sm:px-6 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-5xl mx-auto text-center flex flex-col items-center">
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-6xl font-playfair text-[#0B2A2C] mb-10">
          
            Ready to Begin<br />
            <span className="text-amber-600">Your Journey?</span>
          </motion.h2>

          <p className="text-base sm:text-lg text-gray-600 max-w-sm sm:max-w-xl text-center mb-6 sm:mb-12">
            Limited slots • Personalized care • Life-changing results
          </p>

          <motion.button
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/BookNow")}
            className="bg-[#0B2A2C] text-white px-16 py-6 rounded-full text-xl md:text-2xl font-medium hover:bg-[#1A4D4F] transition-all hover:shadow-2xl hover:scale-105"
          >
            Book Your Consultation
          </motion.button>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HerbalRemedies;
